import { apiBaseUrl, tokenStore } from '../api';
import { ringTones } from '../api/ringTones';
import { getDriverSession } from './driverSession';
import { ringToneStore } from './nativePermissions';

const LOG = '[ringTones]';

/**
 * What a server without the library answers for: no tones, no choice, the
 * built-in tone. An installed app meets an older server after every release
 * (v1.1.12 against live, 5 Oct 2026: the route was not there yet).
 */
export const NO_LIBRARY = Object.freeze({ default_id: null, chosen_id: null, may_choose: false, tones: Object.freeze([]) });

/**
 * The office's tones, brought onto the phone before a job rings (this app's
 * ADR 0014; InGo ADR 0045). The list comes from the API with the rider's
 * token; the ring core fetches each file it lacks, checks it, and records
 * which tone rings. At ring time the core plays its own copy, never the
 * network, and anything missing plays the built-in tone.
 *
 * Synced at sign-in, at app start with a rider signed in, on going online
 * and when the rider opens the tone picker. Every call says what it did,
 * including "nothing" and why: a rider who hears the built-in tone when the
 * office chose another must leave a line that says so.
 */
export function createRingToneSync({ tones, store, baseUrl, token, isSignedIn, log = console }) {
  let running = null;

  async function apply(why, listing) {
    const root = String(baseUrl()).replace(/\/+$/, '');
    const list = Array.isArray(listing?.tones) ? listing.tones : [];
    const result = await store.syncRingTones({
      tones: list.map((t) => ({ id: t.id, sha256: t.sha256, url: `${root}/${String(t.file).replace(/^\/+/, '')}` })),
      defaultId: listing?.default_id ?? null,
      chosenId: listing?.chosen_id ?? null,
      token: await token(),
    });
    log.info(
      `${LOG} synced ${JSON.stringify({
        why,
        listed: list.length,
        have: result?.have ?? [],
        failed: result?.failed ?? [],
        selected: result?.selected ?? null,
      })}`,
    );

    return { listing, result };
  }

  async function run(why) {
    if (!store) {
      log.info(`${LOG} no ring core on this device, nothing to sync ${JSON.stringify({ why })}`);
      return null;
    }
    if (!isSignedIn()) {
      log.info(`${LOG} no rider signed in, nothing to sync ${JSON.stringify({ why })}`);
      return null;
    }
    let listing;
    try {
      listing = await tones.list();
    } catch (e) {
      // The route is not there: this server has no library yet. Not a fault, and the
      // phone's tones are left as they are (a deploy waiting is not a library emptied).
      if (e?.status === 404) {
        log.info(`${LOG} this server has no ringtone library yet, the built-in tone rings ${JSON.stringify({ why })}`);
        return { listing: NO_LIBRARY, result: null };
      }
      log.warn(`${LOG} the list could not be read, the phone keeps its tones ${JSON.stringify({ why, code: e?.code || null })}`);
      return null;
    }
    try {
      return await apply(why, listing);
    } catch (e) {
      log.warn(`${LOG} the ring core could not sync ${JSON.stringify({ why, error: e?.message || String(e) })}`);
      return null;
    }
  }

  return {
    /** Brings the phone in line with the office's list. One at a time: a second call while one runs gets its answer. */
    sync(why) {
      if (!running) {
        running = run(why).finally(() => {
          running = null;
        });
      }
      return running;
    },

    /**
     * The rider's choice (`null` for the default), saved on the server, then
     * synced from the server's answer so the next ring plays it. Refusals
     * reach the caller, which shows the server's sentence.
     */
    async choose(id) {
      const listing = await tones.choose(id);
      if (store) {
        try {
          await apply('chosen', listing);
        } catch (e) {
          log.warn(`${LOG} the ring core could not sync the choice ${JSON.stringify({ error: e?.message || String(e) })}`);
        }
      }

      return listing;
    },
  };
}

export const ringToneSync = createRingToneSync({
  tones: ringTones,
  store: ringToneStore(),
  baseUrl: () => apiBaseUrl(),
  token: () => tokenStore.read(),
  isSignedIn: () => Boolean(getDriverSession()?.id),
});

/** Called at sign-in, app start and going online; it decides, and says, whether there is anything to do. */
export function syncRiderRingTones(why) {
  return ringToneSync.sync(why);
}
